"use client";

import { env } from "@tweetdm/env/client";
import Script from "next/script";
import { create } from "zustand";

declare global {
  interface Window {
    dataLayer: unknown[];
    twq: (command: string, eventId: string, params?: unknown) => void;
    gtag: (command: string, eventId: string | Date, params?: unknown) => void;
  }
}

interface AnalyticsPixelsStore {
  twitterLoaded: boolean;
  setTwitterLoaded: () => void;
  gtagLoaded: boolean;
  setGtagLoaded: () => void;
}

const useAnalyticsPixelsStore = create<AnalyticsPixelsStore>((set) => ({
  twitterLoaded: false,
  setTwitterLoaded: () => set({ twitterLoaded: true }),
  gtagLoaded: false,
  setGtagLoaded: () => set({ gtagLoaded: true }),
}));

const getPixelFunction = async <T extends keyof Window>(
  pixelName: T,
  isLoaded: boolean,
  timeout = 5000,
): Promise<Window[T]> => {
  if (isLoaded && typeof window[pixelName] === "function") {
    return window[pixelName];
  }

  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const checkPixel = () => {
      if (typeof window[pixelName] === "function") {
        resolve(window[pixelName]);
        return;
      }
      if (Date.now() - startTime >= timeout) {
        reject(new Error(`${pixelName} pixel load timeout`));
        return;
      }
      setTimeout(checkPixel, 100);
    };

    checkPixel();
  });
};

export const getTwq = (timeout = 5000) => {
  const { twitterLoaded } = useAnalyticsPixelsStore.getState();
  return getPixelFunction("twq", twitterLoaded, timeout);
};

export const getGtag = (timeout = 5000) => {
  const { gtagLoaded } = useAnalyticsPixelsStore.getState();
  return getPixelFunction("gtag", gtagLoaded, timeout);
};

export const AnalyticsPixels = () => {
  const { setTwitterLoaded, setGtagLoaded } = useAnalyticsPixelsStore();

  return (
    <>
      {env.NEXT_PUBLIC_TWITTER_PIXEL_ID && (
        <Script
          strategy="afterInteractive"
          onLoad={() => setTwitterLoaded()}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: allowed for tracking pixel
          dangerouslySetInnerHTML={{
            __html: `
        !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
        },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
        a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
        twq('config','${env.NEXT_PUBLIC_TWITTER_PIXEL_ID}');
        `,
          }}
        />
      )}
      {env.NEXT_PUBLIC_GTAG_ID && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${env.NEXT_PUBLIC_GTAG_ID}`}
            strategy="afterInteractive"
            onLoad={() => {
              window.dataLayer = window.dataLayer || [];
              // biome-ignore lint/complexity/useArrowFunction: <explanation>
              window.gtag = function () {
                // biome-ignore lint/style/noArguments: <explanation>
                window.dataLayer.push(arguments);
              };

              window.gtag("js", new Date());
              window.gtag(
                "config",
                env.NEXT_PUBLIC_GTAG_ID!,
                env.NEXT_PUBLIC_STAGE === "development"
                  ? { debug_mode: true }
                  : undefined,
              );

              window.gtag("event", "page_view", {
                send_to: `${env.NEXT_PUBLIC_GTAG_ID}/${env.NEXT_PUBLIC_GTAG_PAGE_VIEW_EVENT_ID}`,
              });

              setGtagLoaded();
            }}
          />
          <Script strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());

              gtag('config', '${env.NEXT_PUBLIC_GTAG_ID}');

              gtag('event', 'page_view', {
                'send_to': '${env.NEXT_PUBLIC_GTAG_ID}/${env.NEXT_PUBLIC_GTAG_PAGE_VIEW_EVENT_ID}'
              });
            `}
          </Script>
        </>
      )}
    </>
  );
};
